import { ReadonlyVec2, mat4, vec2, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from 'notistack';
import { Modality } from '~/domain/dicom/modality';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { WindowLevel } from '~/domain/patient/windowLevel';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { ContourOperations } from '~/engine/util/algorithms/sdf/ContourOperations';
import { grayscaleFusionColorMapping } from '~/state/atoms/image/imageAtoms';
import { DoseMatrix } from '../../domain/patient/doseMatrix';
import { Image3D } from '../../domain/patient/image3d';
import { StructureSet } from '../../domain/patient/structureset';
import { ImageViewMode } from '../core/render/renderers/ImageRenderer';
import {
    isInteractiveSDFViewerOpen,
    setSdfToInteractiveSDFViewer,
} from '../util/debugWindow';
import { PanZoom } from '../view/PanZoom';
import { ViewManager } from '../view/ViewManager';
import { VolumeViewUpdateEvent } from '../view/events/VolumeViewUpdateEvent';
import { LayerBit } from '../view/layers/LayerBit';
import { PlaneViewType } from '../view/views/PlaneView';
import { EnvironmentObjects } from './EnvironmentObjects';
import { PointOfInterest } from './PointOfInterest';
import { EnvironmentObject } from './common/EnvironmentObject';
import { CustomTargetObject } from './common/customs/CustomTargetObject';
import { DoseDistribution } from './dose/DoseDistribution';
import { ImageObject } from './image/ImageObject';
import { StructureSetObject } from './structure/StructureSetObject';

export type EnvironmentObjectIds = {
    primaryImage: {
        seriesInstanceUID: string | undefined;
        studyInstanceUID: string | undefined;
    };
    secondaryImage: {
        seriesInstanceUID: string | undefined;
        studyInstanceUID: string | undefined;
    };
    structureSet: {
        sopInstanceUID: string | undefined;
    };
    dose: {
        sopInstanceUID: string | undefined;
    };
};

/**
 * {@link Environment} is a storage class, storing the state of the objects that are currently being viewed.\
 * Environment also stores the common viewing parameters shared between the views.
 */
export class Environment {
    private environmentObjects = new EnvironmentObjects();

    private readonly _panZoom: PanZoom = new PanZoom();

    public get panZoom(): PanZoom {
        return this._panZoom;
    }

    private readonly _planeIntersectionPoint: PointOfInterest =
        new PointOfInterest();

    public get planeIntersectionPoint(): PointOfInterest {
        return this._planeIntersectionPoint;
    }

    public get eventTarget(): EventTarget {
        return this._eventTarget;
    }

    private _windowLevelMode: PlaneViewType = PlaneViewType.Primary;

    public get windowLevelMode(): PlaneViewType {
        return this._windowLevelMode;
    }

    public set windowLevelMode(mode: PlaneViewType) {
        if (this._windowLevelMode !== mode) {
            this._windowLevelMode = mode;
        }
    }

    /**
     * Returns the seriesUid of the currently active windowing target image.
     * Based on the current windowLevelMode (Primary or Secondary).
     */
    public get activeWindowingSeriesUid(): string | undefined {
        const image =
            this._windowLevelMode === PlaneViewType.Primary
                ? this.image?.parent
                : this.secondaryImage?.parent;
        return image?.seriesUid;
    }

    private _alpha: number = 1.0; // primary image alpha

    public get alpha(): number {
        return this._alpha;
    }

    public set alpha(value: number) {
        this._alpha = value;
    }

    private _viewMode: ImageViewMode = ImageViewMode.AlphaBlending;

    public get viewMode(): ImageViewMode {
        return this._viewMode;
    }

    public set viewMode(mode: ImageViewMode) {
        if (this._viewMode !== mode) {
            this._viewMode = mode;
        }
    }

    private _checkerboardDensity = 0.5;

    public get checkerboardDensity(): number {
        return this._checkerboardDensity;
    }

    public set checkerboardDensity(density: number) {
        this._checkerboardDensity = density;
    }

    private _colorMask: vec3 = grayscaleFusionColorMapping.primaryValues;

    public get colorMask(): vec3 {
        return this._colorMask;
    }

    public set colorMask(value: vec3) {
        this._colorMask = value;
    }

    private _secondaryColorMask: vec3 =
        grayscaleFusionColorMapping.secondaryValues;

    public get secondaryColorMask(): vec3 {
        return this._secondaryColorMask;
    }

    public set secondaryColorMask(value: vec3) {
        this._secondaryColorMask = value;
    }

    private _petFusionColorMapTypeIdx: number = 0;

    public get petFusionColorMapTypeIdx(): number {
        return this._petFusionColorMapTypeIdx;
    }

    public set petFusionColorMapTypeIdx(value: number) {
        this._petFusionColorMapTypeIdx = value;
    }

    // save changes to domain data model
    public saveChanges() {
        this.environmentObjects.structureSet?.saveChanges();
    }

    public constructor(private readonly _eventTarget: EventTarget) {}

    public setStructureSet(ss?: StructureSet): EnvironmentObjects | undefined {
        const { structureSet: currentSS, dose: currentDose } =
            this.environmentObjects;

        if (!ss) {
            this.removeStructureSetInternal();
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
            return this.environmentObjects;
        }

        if (!ss.image) {
            enqueueSnackbar(
                `Structure set ${ss.structureSetLabel} is not connected to an image.`,
                { variant: 'error' }
            );
            return;
        }

        if (currentSS?.parent === ss) {
            return this.environmentObjects;
        }

        if (currentSS) {
            this.removeStructureSetInternal();
        }
        if (currentDose) {
            this.removeDoseInternal();
        }

        this.setImage(ss.image);

        const newStructureSet = new StructureSetObject(ss);
        this.environmentObjects.structureSet = newStructureSet;

        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );

        for (const roi of newStructureSet.structures) {
            if (roi.renderErrors) {
                enqueueSnackbar(
                    `Error rendering structure ${roi.name}: ${roi.renderErrors}`,
                    { variant: 'error' }
                );
            }
        }

        return this.environmentObjects;
    }

    public setDose(dose: DoseMatrix | undefined) {
        if (this.environmentObjects.dose) {
            if (this.environmentObjects.dose.parent === dose) {
                return this.environmentObjects;
            }
            this.removeDoseInternal();
        }
        if (dose) {
            if (!dose.structureSet) {
                enqueueSnackbar(
                    `Dose ${dose.dicomUid} is not connected to a structure set.`,
                    { variant: 'error' }
                );
                return this.environmentObjects;
            }
            if (!dose.structureSet.image) {
                enqueueSnackbar(
                    `Cannot load dose: the associated image is not available.`,
                    { variant: 'error' }
                );
                return this.environmentObjects;
            }
            const ssResult = this.setStructureSet(dose.structureSet);
            if (!ssResult) {
                return this.environmentObjects;
            }
            this.environmentObjects.dose = new DoseDistribution(dose);
        }
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.dose,
                LayerBit.DOSE_LAYER_BIT,
                true
            )
        );
        return this.environmentObjects;
    }

    public updateSelectedDoseRange(range: ReadonlyVec2) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.selectedDoseRange = range;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.dose,
                    LayerBit.DOSE_LAYER_BIT,
                    true
                )
            );
        }
    }

    public updateDoseAlphaLevelOpacity(opacity: number) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.alphaLevelOpacity = opacity;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.dose,
                    LayerBit.DOSE_LAYER_BIT,
                    true
                )
            );
        }
    }

    public updateDoseVisibility(visible: boolean) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.isVisible = visible;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.dose,
                    LayerBit.DOSE_LAYER_BIT,
                    true
                )
            );
        }
    }

    public setImage(image: Image3D | undefined) {
        if (this.environmentObjects.primaryImage) {
            if (this.environmentObjects.primaryImage.parent === image) {
                return;
            }
            this.removeImagesInternal();
        }
        if (this.environmentObjects.dose) {
            this.removeDoseInternal();
        }
        if (this.environmentObjects.structureSet) {
            this.removeStructureSetInternal();
        }
        if (image) {
            this.environmentObjects = new EnvironmentObjects();
            this.environmentObjects.primaryImage = ImageObject.fromImage3D(
                image,
                image.firstSlice
            );
            ViewManager.resetViews();
        } else {
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.primaryImage,
                    LayerBit.ALL_LAYERS,
                    true
                )
            );
        }
        return this.environmentObjects;
    }

    public setRegistration(
        registration: RigidRegistration | undefined
    ): EnvironmentObjects {
        this.environmentObjects.registration = registration;
        this._eventTarget.dispatchEvent(new Event('registration_changed'));
        return this.environmentObjects;
    }

    public setRegistrationMatrix(newMatrix: mat4) {
        if (!this.registration || this.registration.isApproved) {
            return;
        }
        mat4.copy(this.registration.transformation, newMatrix);

        this._eventTarget.dispatchEvent(new Event('registration_changed'));
    }

    private _operationPreviewStructure?: CustomTargetObject;
    public set operationPreviewStructure(
        preview: CustomTargetObject | undefined
    ) {
        if (this._operationPreviewStructure) {
            this._operationPreviewStructure.delete();
        }
        this._operationPreviewStructure = preview;
    }

    public get operationPreviewStructure(): CustomTargetObject | undefined {
        return this._operationPreviewStructure;
    }

    public changeFusionColorMapping(
        primaryValues: vec3,
        secondaryValues: vec3
    ) {
        this.colorMask = primaryValues;
        this.secondaryColorMask = secondaryValues;

        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.image,
                LayerBit.IMAGE_LAYER_BIT,
                true
            )
        );
    }

    public changePetFusionColorMapping(colorMapTypeIdx: number) {
        if (!this.image) {
            return;
        }
        this.petFusionColorMapTypeIdx = colorMapTypeIdx;
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.image,
                LayerBit.IMAGE_LAYER_BIT,
                true
            )
        );
    }

    private removeImagesInternal() {
        if (this.environmentObjects.secondaryImage) {
            this.removeObject(this.environmentObjects.secondaryImage);
            this.environmentObjects.secondaryImage = undefined;
        }
        if (this.environmentObjects.primaryImage) {
            this.removeObject(this.environmentObjects.primaryImage);
            this.environmentObjects.primaryImage = undefined;
        }
    }

    private removeEnvironmentSecondaryImageInternal() {
        if (this.environmentObjects.secondaryImage) {
            this.removeObject(this.environmentObjects.secondaryImage);
            this.environmentObjects.secondaryImage = undefined;
        }
    }

    private removeDoseInternal() {
        if (this.environmentObjects.dose) {
            this.removeObject(this.environmentObjects.dose);
            this.environmentObjects.dose = undefined;
        }
    }

    private removeStructureSetInternal() {
        if (this.environmentObjects.structureSet) {
            const ss = this.environmentObjects.structureSet;
            try {
                ss.saveChanges();
            } catch (e) {
                enqueueSnackbar(
                    `Error saving structure set changes: ${ss.displayName}, ${e}.`,
                    { variant: 'error' }
                );
            }
            this.removeObject(ss);
            this.setSelectedStructure(undefined);
            this.environmentObjects.structureSet = undefined;
        }
    }

    public clearContourOnSlice(roiNumber: number, planeCoordinates: number) {
        if (this.environmentObjects.structureSet) {
            const imgObject = this.environmentObjects.primaryImage;
            this.environmentObjects.structureSet.clearContoursOnSlice(
                roiNumber,
                planeCoordinates,
                imgObject
            );
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public clearAllContours(roiNumber: number) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.clearAllContours(roiNumber);
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public keepEveryNthSlice(
        roiNumber: number,
        keepEveryNthSliceValue: number
    ) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.keepEveryNthSlice(
                roiNumber,
                keepEveryNthSliceValue
            );
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    /**
     * Triggers a view update event for the contour layer.
     * This method provides a centralized way to notify the ViewManager
     * that the contour layer needs to be re-rendered.
     */
    public triggerContourLayerUpdate() {
        if (this.environmentObjects.structureSet) {
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public setSecondaryImage(
        image: Image3D | undefined,
        registration?: RigidRegistration
    ) {
        if (!this.environmentObjects.primaryImage) {
            return;
        }
        if (this.environmentObjects.secondaryImage) {
            this.removeEnvironmentSecondaryImageInternal();
        }

        if (image) {
            if (
                !this.canBePickedAsSecondary(
                    this.environmentObjects.primaryImage.parent,
                    image,
                    registration
                )
            ) {
                return;
            }
            const transformation = registration?.getTransformFromTo(
                image.frameOfReferenceUid,
                this.environmentObjects.primaryImage.parent.frameOfReferenceUid
            );

            // Only throw error if registration was provided but transformation failed
            if (!transformation && registration) {
                throw new Error(
                    'Could not construct transformation matrix for secondary image.'
                );
            }
            this.environmentObjects.secondaryImage = ImageObject.fromImage3D(
                image,
                image.firstSlice,
                transformation
            );
            ViewManager.resetViews();
        }
        this._eventTarget.dispatchEvent(new Event('image_changed'));
        return this.environmentObjects;
    }

    public updateSecondaryTransformation(): void {
        const primaryImage = this.environmentObjects.primaryImage?.parent;
        const secondaryImageObj = this.environmentObjects.secondaryImage;
        const registration = this.registration;

        if (!primaryImage || !secondaryImageObj || !registration) {
            // No-op when registration is absent (same-FOR images
            // don't need a transformation) or when images are not
            // loaded yet.
            return;
        }

        const newTransformationMatrix = registration?.getTransformFromTo(
            secondaryImageObj.parent.frameOfReferenceUid,
            primaryImage.frameOfReferenceUid
        );

        if (!newTransformationMatrix) {
            console.error('Could not construct transformation matrix.');
            return;
        }

        secondaryImageObj.updateGridTransformation(newTransformationMatrix);

        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.secondaryImage,
                LayerBit.IMAGE_LAYER_BIT,
                true
            )
        );

        this._eventTarget.dispatchEvent(new Event('image_changed'));
    }

    public updatePrimaryImageAlpha(alpha: number) {
        if (!this.image) {
            return;
        }
        if (alpha >= 0 && alpha <= 1) {
            this.alpha = alpha;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.image,
                    LayerBit.IMAGE_LAYER_BIT,
                    true
                )
            );
        }
    }

    public updateCheckerboardDensity(density: number) {
        if (!this.image) {
            return;
        }
        this.checkerboardDensity = density;
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.image,
                LayerBit.IMAGE_LAYER_BIT,
                true
            )
        );
    }

    public updateImageViewMode(mode: ImageViewMode) {
        if (!this.image) {
            return;
        }
        this.viewMode = mode;
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.image,
                LayerBit.IMAGE_LAYER_BIT,
                true
            )
        );
    }

    /**
     * Determines whether `image2` can be used as a secondary (fusion) image
     * for `image1`.
     *
     * The rules are:
     * - Same Frame of Reference → always allowed.
     * - Same study with CT + PET modality pair → allowed (SUV fusion).
     * - Otherwise a registration that links the two FORs must exist.
     */
    private canBePickedAsSecondary(
        image1: Image3D,
        image2: Image3D,
        registration?: RigidRegistration
    ): boolean {
        if (image1.frameOfReferenceUid === image2.frameOfReferenceUid) {
            return true;
        }
        // Allow CT+PET fusion if they are from the same study
        if (image1.studyUid === image2.studyUid) {
            const modality1 = image1.seriesMetadata.modality;
            const modality2 = image2.seriesMetadata.modality;
            if (
                (modality1 === Modality.CT && modality2 === Modality.PT) ||
                (modality1 === Modality.PT && modality2 === Modality.CT)
            ) {
                return true;
            }
        }
        if (!registration) {
            return false;
        }
        // getTransformFromTo returns undefined when the FORs are unrelated
        const transform = registration.getTransformFromTo(
            image2.frameOfReferenceUid,
            image1.frameOfReferenceUid
        );
        return transform !== undefined;
    }

    private removeObject(obj: EnvironmentObject) {
        obj.delete();
    }

    public get structureSet(): StructureSetObject | undefined {
        return this.environmentObjects.structureSet;
    }

    public get dose(): DoseDistribution | undefined {
        return this.environmentObjects.dose;
    }
    public get image(): ImageObject | undefined {
        return this.environmentObjects.primaryImage;
    }

    public get registration(): RigidRegistration | undefined {
        return this.environmentObjects.registration;
    }

    public get secondaryImage(): ImageObject | undefined {
        return this.environmentObjects.secondaryImage;
    }

    public get getEnvironmentObjectIds(): EnvironmentObjectIds {
        const environmentObjectIds: Partial<EnvironmentObjectIds> = {};

        if (this.environmentObjects.primaryImage) {
            environmentObjectIds.primaryImage = {
                seriesInstanceUID:
                    this.environmentObjects.primaryImage.parent.seriesUid,
                studyInstanceUID:
                    this.environmentObjects.primaryImage.parent.studyUid,
            };
        }

        if (this.environmentObjects.secondaryImage) {
            environmentObjectIds.secondaryImage = {
                seriesInstanceUID:
                    this.environmentObjects.secondaryImage.parent.seriesUid,
                studyInstanceUID:
                    this.environmentObjects.secondaryImage.parent.studyUid,
            };
        }

        if (this.environmentObjects.structureSet) {
            environmentObjectIds.structureSet = {
                sopInstanceUID:
                    this.environmentObjects.structureSet.sopInstanceUID,
            };
        }

        if (this.environmentObjects.dose) {
            environmentObjectIds.dose = {
                sopInstanceUID: this.environmentObjects.dose.sopInstanceUID,
            };
        }

        return environmentObjectIds as EnvironmentObjectIds;
    }

    /** Update the selected structure and update views */
    public setSelectedStructure(selectedROI: RegionOfInterest | undefined) {
        if (!this.environmentObjects.structureSet) {
            return;
        }
        if (
            this.environmentObjects.structureSet.selectedStructure ===
            selectedROI
        ) {
            return;
        }
        this.environmentObjects.structureSet.selectedStructure = selectedROI;
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
        if (
            isInteractiveSDFViewerOpen() &&
            selectedROI?.texture &&
            selectedROI.texture.signedDistanceField
        ) {
            setSdfToInteractiveSDFViewer(
                selectedROI.texture.signedDistanceField,
                selectedROI.name
            );
        }
    }

    public setSelectedRoiByNumber(roiNumber: number) {
        if (!this.environmentObjects.structureSet) {
            return;
        }
        if (roiNumber === -1) {
            this.setSelectedStructure(undefined);
            return;
        }
        const roi = this.environmentObjects.structureSet.getRoi(roiNumber);
        if (roi) {
            this.setSelectedStructure(roi);
        }
    }

    public getRoi(roiNumber: number): RegionOfInterest | undefined {
        return this.environmentObjects.structureSet?.getRoi(roiNumber);
    }

    public getRoiVolumeCm3(roiNumber: number): number {
        return (
            this.environmentObjects.structureSet?.getRoi(roiNumber)
                ?.volumeCm3 ?? Number.NaN
        );
    }

    public isRoiEmpty(roiNumber: number): boolean {
        return (
            this.environmentObjects.structureSet?.getRoi(roiNumber)?.isEmpty ??
            true
        );
    }

    /**
     * Sets the window level of an image identified by its DICOM Series Instance UID.
     * @param level - The new window level values
     * @param seriesUid - The DICOM Series Instance UID of the image to update
     */
    public setImageWindowLevel(level: WindowLevel, seriesUid: string): void {
        const image = this.findImageBySeriesUid(seriesUid);
        this.applyWindowLevel(image, level);
    }

    /**
     * Adjusts the window level of an image by a delta amount.
     * @param delta - The delta values [windowDelta, levelDelta]
     * @param seriesUid - The DICOM Series Instance UID of the image to update
     */
    public adjustImageWindowLevel(delta: vec2, seriesUid: string): void {
        const image = this.findImageBySeriesUid(seriesUid);
        if (image?.windowLevel) {
            this.applyWindowLevel(image, image.windowLevel.add(delta));
        }
    }

    private applyWindowLevel(
        image: ImageObject | undefined,
        level: WindowLevel
    ): void {
        if (image && image.windowLevel && !image.windowLevel.equals(level)) {
            image.windowLevel = level;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(image, LayerBit.ALL_LAYERS, true)
            );
        }
    }

    /**
     * Returns the current window level for the image identified
     * by its parent Image3D's seriesUid.
     */
    public getImageWindowLevel(seriesUid: string): WindowLevel | undefined {
        return this.findImageBySeriesUid(seriesUid)?.windowLevel;
    }

    /**
     * Finds an ImageObject by its parent Image3D's seriesUid.
     */
    private findImageBySeriesUid(seriesUid: string): ImageObject | undefined {
        if (
            this.environmentObjects.primaryImage?.parent?.seriesUid ===
            seriesUid
        ) {
            return this.environmentObjects.primaryImage;
        }
        if (
            this.environmentObjects.secondaryImage?.parent?.seriesUid ===
            seriesUid
        ) {
            return this.environmentObjects.secondaryImage;
        }
        return undefined;
    }

    public handleInterpolateSingleSlice(
        roi: RegionOfInterest,
        planeCoordinates: number
    ) {
        const primaryImage = this.environmentObjects.primaryImage;
        if (!primaryImage) {
            return;
        }
        const contourOperations = new ContourOperations();
        contourOperations.handleInterpolateSingleSlice(
            roi,
            primaryImage,
            planeCoordinates
        );
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    public handleInterpolateAllSlices(roi: RegionOfInterest) {
        const contourOperations = new ContourOperations();
        contourOperations.handleInterpolateAllSlices(roi);
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }
}
